/**
 * SKOP Sentiment Card
 *
 * Compact display of social sentiment for a token.
 */

import { useState, useEffect } from 'react';
import {
  getTokenSentiment,
  type TokenSentiment,
  getSentimentColor,
  getSentimentLabel,
  getTrendIcon,
  getTrendColor,
} from '../lib/sentiment';
import { SentimentDetails } from './SentimentDetails';

interface SentimentCardProps {
  tokenAddress: string;
}

export function SentimentCard({ tokenAddress }: SentimentCardProps) {
  const [sentiment, setSentiment] = useState<TokenSentiment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTokenSentiment(tokenAddress)
      .then(setSentiment)
      .finally(() => setLoading(false));
  }, [tokenAddress]);

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          <span style={{ fontSize: '13px', fontWeight: 600 }}>Social Sentiment</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          color: 'var(--text-tertiary)',
          fontSize: '12px',
        }}>
          <div
            className="animate-spin"
            style={{
              width: '14px',
              height: '14px',
              border: '2px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              marginRight: '8px',
            }}
          />
          Analyzing social sentiment...
        </div>
      </div>
    );
  }

  if (!sentiment) {
    return null;
  }

  return (
    <>
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>Social Sentiment</span>
            <span style={{
              fontSize: '16px',
              color: getTrendColor(sentiment.trend),
            }}>
              {getTrendIcon(sentiment.trend)}
            </span>
          </div>
          <span style={{
            padding: '2px 8px',
            fontSize: '10px',
            fontWeight: 600,
            background: `${getSentimentColor(sentiment.overallScore)}15`,
            color: getSentimentColor(sentiment.overallScore),
            borderRadius: 'var(--radius-sm)',
          }}>
            {sentiment.confidence.toUpperCase()}
          </span>
        </div>

        {/* Score Display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}>
          <div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: getSentimentColor(sentiment.overallScore),
              lineHeight: 1,
            }}>
              {sentiment.overallScore > 0 ? '+' : ''}{sentiment.overallScore}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              marginTop: '4px',
            }}>
              {getSentimentLabel(sentiment.overallScore)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>
              {sentiment.mentionCount24h.toLocaleString()}
            </div>
            <div style={{
              fontSize: '11px',
              color: sentiment.mentionCountChange >= 0 ? 'var(--success)' : 'var(--error)',
            }}>
              {sentiment.mentionCountChange >= 0 ? '+' : ''}{sentiment.mentionCountChange}% 24h
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--text-tertiary)',
            }}>
              mentions
            </div>
          </div>
        </div>

        {/* Sentiment Breakdown Bar */}
        <div style={{
          display: 'flex',
          height: '8px',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '12px',
        }}>
          <div style={{
            width: `${sentiment.breakdown.positive}%`,
            background: '#00ff88',
          }} />
          <div style={{
            width: `${sentiment.breakdown.neutral}%`,
            background: '#888',
          }} />
          <div style={{
            width: `${sentiment.breakdown.negative}%`,
            background: '#ff3b3b',
          }} />
        </div>

        {/* Breakdown Labels */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: 'var(--text-tertiary)',
          marginBottom: '12px',
        }}>
          <span>
            <span style={{ color: '#00ff88' }}>●</span> {sentiment.breakdown.positive}% positive
          </span>
          <span>
            <span style={{ color: '#888' }}>●</span> {sentiment.breakdown.neutral}% neutral
          </span>
          <span>
            <span style={{ color: '#ff3b3b' }}>●</span> {sentiment.breakdown.negative}% negative
          </span>
        </div>

        {/* Top Keywords */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginBottom: '12px',
        }}>
          {sentiment.topKeywords.slice(0, 5).map((keyword) => (
            <span
              key={keyword}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)',
              }}
            >
              {keyword}
            </span>
          ))}
        </div>

        {/* View Details Button */}
        <button
          onClick={() => setShowDetails(true)}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          View Mentions & Trends
        </button>
      </div>

      {/* Details Modal */}
      {showDetails && (
        <SentimentDetails
          sentiment={sentiment}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
}
